import type { PaletteRamp, PaletteStep } from './types';
import { PALETTE_STEPS } from './types';

export function hexToRgb(hex: string): { r: number; g: number; b: number } {
  const h = hex.replace('#', '');
  const full = h.length === 3 ? h.split('').map((c) => c + c).join('') : h;
  const n = parseInt(full, 16);
  return { r: (n >> 16) & 255, g: (n >> 8) & 255, b: n & 255 };
}

export function rgbToHex(r: number, g: number, b: number): string {
  const clamp = (v: number) => Math.max(0, Math.min(255, Math.round(v)));
  return `#${[clamp(r), clamp(g), clamp(b)]
    .map((x) => x.toString(16).padStart(2, '0'))
    .join('')}`;
}

/** Linear mix between two hex colours (sRGB). */
export function mixHex(a: string, b: string, t: number): string {
  const A = hexToRgb(a);
  const B = hexToRgb(b);
  return rgbToHex(
    A.r + (B.r - A.r) * t,
    A.g + (B.g - A.g) * t,
    A.b + (B.b - A.b) * t,
  );
}

/** 10-step ramp: 10 = `light`, 100 = `dark`. */
export function linearRamp(light: string, dark: string): PaletteRamp {
  const out = {} as Record<PaletteStep, string>;
  PALETTE_STEPS.forEach((step, i) => {
    const t = i / (PALETTE_STEPS.length - 1);
    out[step] = mixHex(light, dark, t);
  });
  return out as PaletteRamp;
}

/**
 * Dark-column ramp from a light-column ramp: step 10 is darkest, 100 lightest
 * (matches “Dark palette” inversion in the spec images).
 */
export function invertRamp(lightColumn: PaletteRamp): PaletteRamp {
  const steps = [...PALETTE_STEPS];
  const out = {} as Record<PaletteStep, string>;
  steps.forEach((step, i) => {
    const mirror = steps[steps.length - 1 - i];
    out[step] = lightColumn[mirror];
  });
  return out as PaletteRamp;
}

/** Space-separated `R G B` for Tailwind `rgb(var(--x) / <alpha>)`. */
export function hexToSpaceSeparatedRgb(hex: string): string {
  const { r, g, b } = hexToRgb(hex);
  return `${r} ${g} ${b}`;
}
